import {
  parseDocument,
  IMPORT_LIMITS
} from "../documentParser/index.js";
import {
  extractQuestionsFromChunk,
  parseJsonFromContent,
  LlmConfigError
} from "./llmClient.js";
import {
  buildChunkUserContent,
  buildTextOnlyUserContent,
  MAX_IMAGES_PER_REQUEST
} from "./prompt.js";
import { getImageDataUri } from "./imagePrep.js";
import { parseLlmQuestions } from "./schema.js";

export { LlmConfigError };

const CHUNK_MAX_QUESTIONS = 8;
const CHUNK_MAX_CHARS = 9000;

// The extraction service times out after ~2 minutes upstream, so one giant
// request for a whole document never completes. A section larger than a
// chunk is therefore split on line boundaries — preferring question starts —
// before chunking.
const SECTION_SPLIT_CHARS = 4500;

export class SmartImportError extends Error {}

// A section starts at a question-ish boundary; trailing units (captions,
// continuation paragraphs, speaker notes) attach to the previous boundary.
// PPTX answer slides repeat the "Question N" title, so a boundary whose
// question number equals the current section's is a continuation, not a
// new question.
const QUESTION_NUMBER_START_RE = /^\s*question\s*(\d+)/i;
const NUMBERED_START_RE = /^\s*(\d+)\s*[).]\s*\S/;

export function buildSections(units) {
  const sections = [];
  let lastQuestionNumber = null;

  for (const unit of units) {
    const boundaryNumber = questionStartNumber(unit.text);
    const startsQuestion = boundaryNumber !== null;
    const continuesCurrentQuestion =
      startsQuestion &&
      boundaryNumber !== null &&
      boundaryNumber === lastQuestionNumber;

    if (
      !sections.length ||
      (startsQuestion && !continuesCurrentQuestion)
    ) {
      sections.push({
        label: unit.label,
        texts: [],
        imageIds: [],
        // imageId -> starting line index of the unit that carried it, so a
        // forced split can keep each image with the text it belongs to
        imageLine: {},
        cursor: 0
      });
      lastQuestionNumber = boundaryNumber;
    }
    const section = sections[sections.length - 1];
    for (const imageId of unit.imageIds) {
      section.imageLine[imageId] = section.cursor;
    }
    if (unit.text) {
      section.texts.push(unit.text);
      // join("\n") separates lines without adding any, so the next unit's
      // starting line is simply the running total of lines so far
      section.cursor += unit.text.split("\n").length;
    }
    section.imageIds.push(...unit.imageIds);
  }

  return sections.map((section) => ({
    label: section.label,
    text: section.texts.join("\n"),
    // question + answer slides often embed the same picture; keep order, drop dupes
    imageIds: [...new Set(section.imageIds)],
    imageLine: section.imageLine
  }));
}

function questionStartNumber(text) {
  const match =
    text.match(QUESTION_NUMBER_START_RE) ||
    text.match(NUMBERED_START_RE);
  return match ? Number(match[1]) : null;
}

// A line that plausibly opens a question: either the explicit numbered
// forms above, or a stem sentence — auto-numbered Word lists carry no
// number in their text, but their stems almost always end with "?".
const looksLikeStem = (text) =>
  questionStartNumber(text) !== null || /\?\s*$/.test(text.trim());

/**
 * Split a section whose text exceeds SECTION_SPLIT_CHARS into parts, each
 * small enough to fit inside a chunk on its own. Breaks at line boundaries
 * and prefers to close a part just before a question-ish line, so a question
 * is not usually cut in half. Each image stays with the part containing the
 * line it appeared on in the source.
 */
export function splitOversizedSection(section) {
  if (section.text.length <= SECTION_SPLIT_CHARS) return [section];

  const lines = section.text.split("\n");
  const parts = [];
  let buffer = [];
  let bufferStart = 0;
  let charCount = 0;
  let lineIndex = 0;
  const imageLine = section.imageLine || {};

  const imagesInBuffer = () =>
    (section.imageIds || []).filter((id) => {
      const at = imageLine[id];
      return at !== undefined && at >= bufferStart && at < lineIndex;
    }).length;

  const flush = (upTo = buffer.length) => {
    if (upTo > 0 && buffer.length) {
      parts.push({
        lines: buffer.slice(0, upTo),
        startLine: bufferStart,
        endLine: bufferStart + upTo
      });
      buffer = buffer.slice(upTo);
      bufferStart += upTo;
      charCount = buffer.join("\n").length;
    }
  };

  for (const line of lines) {
    if (
      (charCount + line.length + 1 > SECTION_SPLIT_CHARS ||
        imagesInBuffer() >= MAX_IMAGES_PER_REQUEST) &&
      buffer.length
    ) {
      // prefer breaking just before the last question-ish line in the buffer
      let cut = -1;
      for (
        let i = Math.max(1, buffer.length - 30);
        i < buffer.length;
        i++
      ) {
        if (looksLikeStem(buffer[i])) cut = i;
      }
      flush(cut > 0 ? cut : buffer.length);
    }

    // A single line longer than the cap cannot be split on a boundary;
    // emit it as its own part(s) so chunking still progresses.
    if (line.length > SECTION_SPLIT_CHARS) {
      flush();
      for (let i = 0; i < line.length; i += SECTION_SPLIT_CHARS) {
        parts.push({
          lines: [line.slice(i, i + SECTION_SPLIT_CHARS)],
          startLine: lineIndex,
          endLine: lineIndex + 1
        });
      }
      lineIndex += 1;
      continue;
    }

    buffer.push(line);
    charCount += line.length + 1;
    lineIndex += 1;
  }
  flush();

  const imageLine2 = section.imageLine || {};
  return parts.map((part, index) => ({
    label: section.label,
    text: part.lines.join("\n"),
    imageIds: (section.imageIds || []).filter((imageId) => {
      const at = imageLine2[imageId];
      // unknown anchor (e.g. older callers) falls back to the last part
      if (at === undefined) return index === parts.length - 1;
      return at >= part.startLine && at < part.endLine;
    })
  }));
}

export function buildChunks(sections) {
  const chunkSections = sections.flatMap(splitOversizedSection);

  const chunks = [];
  let current = [];
  let charCount = 0;
  let imageCount = 0;

  for (const section of chunkSections) {
    const sectionLength = section.text.length;
    const sectionImages = new Set(section.imageIds).size;
    const wouldOverflow =
      current.length >= CHUNK_MAX_QUESTIONS ||
      charCount + sectionLength > CHUNK_MAX_CHARS ||
      imageCount + sectionImages > MAX_IMAGES_PER_REQUEST;
    if (current.length && wouldOverflow) {
      chunks.push(current);
      current = [];
      charCount = 0;
      imageCount = 0;
    }
    current.push(section);
    charCount += sectionLength;
    imageCount += sectionImages;
  }
  if (current.length) chunks.push(current);
  return chunks;
}

function postProcess(questions) {
  const unique = [];
  const seenNumbers = new Set();
  for (const question of questions) {
    const key = question.number;
    if (key != null && seenNumbers.has(key)) continue;
    if (key != null) seenNumbers.add(key);
    unique.push(question);
  }
  const truncated = unique.length > IMPORT_LIMITS.maxQuestions;
  return {
    questions: unique.slice(0, IMPORT_LIMITS.maxQuestions),
    truncated
  };
}

// The extraction service caps the model's output tokens; a reply that is cut
// off mid-JSON comes back as a 502 "The extraction was incomplete. Try a
// smaller document." Chunks that trip it are split in half and each half is
// retried, which is exactly what the message prescribes.
const INCOMPLETE_EXTRACTION_RE = /extraction was incomplete/i;
const MAX_BISECT_DEPTH = 4;

async function extractSections({
  sections,
  images,
  signal,
  startNumber,
  textOnly
}) {
  const imageDataUris = {};
  if (!textOnly) {
    for (const imageId of new Set(
      sections.flatMap((section) => section.imageIds)
    )) {
      const image = images[imageId];
      if (image) {
        imageDataUris[imageId] = await getImageDataUri(image.file);
      }
    }
  }

  const { content } = textOnly
    ? buildTextOnlyUserContent(sections, { startNumber })
    : buildChunkUserContent(sections, imageDataUris, {
        startNumber
      });

  const reply = await extractQuestionsFromChunk({
    userContent: content,
    signal
  });
  return parseLlmQuestions(parseJsonFromContent(reply));
}

async function extractWithBisect(params) {
  const { sections, signal, depth = 0 } = params;
  try {
    return await extractSections(params);
  } catch (error) {
    const canBisect =
      !signal?.aborted &&
      INCOMPLETE_EXTRACTION_RE.test(error.message || "") &&
      sections.length > 1 &&
      depth < MAX_BISECT_DEPTH;
    if (!canBisect) throw error;

    const mid = Math.ceil(sections.length / 2);
    const first = await extractWithBisect({
      ...params,
      sections: sections.slice(0, mid),
      depth: depth + 1
    });
    const second = await extractWithBisect({
      ...params,
      sections: sections.slice(mid),
      startNumber: params.startNumber + first.length,
      depth: depth + 1
    });
    return [...first, ...second];
  }
}

/**
 * Run the extraction loop over a list of chunks. `chunkLabels` carries the
 * batch number shown to the user for each chunk (the original numbering,
 * also when a retry re-runs a subset), and `startNumber` is the number the
 * first extracted question continues from.
 */
async function extractChunks({
  chunks,
  images,
  signal,
  onProgress,
  startNumber,
  chunkLabels,
  chunkCount
}) {
  const questions = [];
  const failedChunks = [];
  let textOnlyChunks = 0;

  for (let i = 0; i < chunks.length; i++) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    const chunkSections = chunks[i];
    const label = chunkLabels[i];
    const chunkImageIds = [
      ...new Set(chunkSections.flatMap((section) => section.imageIds))
    ];
    onProgress({
      phase: "extracting",
      chunkIndex: label - 1,
      chunkCount
    });

    try {
      const parsed = await extractWithBisect({
        sections: chunkSections,
        images,
        signal,
        startNumber: startNumber + questions.length
      });
      questions.push(...parsed);
    } catch (error) {
      if (signal?.aborted || error instanceof LlmConfigError) {
        throw error;
      }
      if (error.message === "VISION_UNSUPPORTED") {
        // Model/provider rejected images: retry text-only, then attach this
        // chunk's images to stems positionally (source order). This retry
        // stays inside the per-chunk boundary, so a failure here loses only
        // this chunk rather than the whole run.
        onProgress({
          phase: "retrying",
          chunkIndex: label,
          chunkCount,
          message: `Batch ${label} rejected images — retrying it without them`
        });
        try {
          const parsed = await extractWithBisect({
            sections: chunkSections,
            images,
            signal,
            startNumber: startNumber + questions.length,
            textOnly: true
          });
          assignPositionalImages(parsed, chunkImageIds);
          questions.push(...parsed);
          if (chunkImageIds.length) textOnlyChunks++;
        } catch (fallbackError) {
          if (signal?.aborted) throw fallbackError;
          onProgress({
            phase: "batchFailed",
            chunkIndex: label,
            chunkCount,
            message: `Batch ${label} failed — its questions are missing. You can retry failed batches at the end`
          });
          failedChunks.push({
            index: label,
            error: fallbackError.message
          });
        }
        onProgress({
          phase: "extracting",
          chunkIndex: label,
          chunkCount
        });
        continue;
      }
      onProgress({
        phase: "batchFailed",
        chunkIndex: label,
        chunkCount,
        message: `Batch ${label} failed — retrying it before moving on`
      });
      failedChunks.push({ index: label, error: error.message });
    }
    onProgress({
      phase: "extracting",
      chunkIndex: label,
      chunkCount
    });
  }

  return { questions, failedChunks, textOnlyChunks };
}

function computeStats(questions) {
  return {
    total: questions.length,
    inferred: questions.filter((q) => q.answer_source === "inferred")
      .length,
    unclear: questions.filter((q) => q.answer_source === "unclear")
      .length,
    withImages: questions.filter(
      (q) =>
        (q.image_ids?.length || 0) > 0 ||
        q.options.some((o) => o.image_id)
    ).length
  };
}

function finalizeResult(questions, truncated) {
  const ordered = questions.sort(
    (a, b) => (a.number ?? 0) - (b.number ?? 0)
  );
  const post = postProcess(ordered);
  return {
    questions: post.questions,
    truncated: truncated || post.truncated
  };
}

/**
 * Full pipeline: parse -> slice -> chunked LLM extraction -> validated JSON.
 * @param {Object} params
 * @param {File} params.file
 * @param {AbortSignal} [params.signal]
 * @param {(progress: Object) => void} [params.onProgress]
 * @returns {Promise<{fileName: string, kind: string, questions: Array,
 *   images: Object, failedChunks: Array, textOnlyChunks: number, truncated: boolean,
 *   chunks: Array, stats: {total: number, inferred: number, unclear: number, withImages: number}}>}
 */
export async function runSmartImport({
  file,
  signal,
  onProgress = () => {}
}) {
  onProgress({ phase: "parsing" });
  const { fileName, kind, units, images } = await parseDocument(file);

  const sections = buildSections(units);
  if (!sections.length) {
    throw new SmartImportError(
      "Could not find any question-like content in this file."
    );
  }
  const chunks = buildChunks(sections);

  const { questions, failedChunks, textOnlyChunks } =
    await extractChunks({
      chunks,
      images,
      signal,
      onProgress,
      startNumber: 1,
      chunkLabels: chunks.map((_, i) => i + 1),
      chunkCount: chunks.length
    });

  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const final = finalizeResult(questions, false);
  onProgress({ phase: "done" });

  return {
    fileName,
    kind,
    questions: final.questions,
    images,
    failedChunks,
    textOnlyChunks,
    truncated: final.truncated,
    // kept so a failed-batches retry can re-run only what failed
    chunks,
    stats: computeStats(final.questions)
  };
}

/**
 * Re-run only the batches that failed in a previous run, keeping the
 * questions already extracted. Numbering continues after the kept
 * questions so the post-run dedup cannot drop anything.
 * @param {Object} params
 * @param {ReturnType<typeof runSmartImport>} params.result a previous result
 * @param {AbortSignal} [params.signal]
 * @param {(progress: Object) => void} [params.onProgress]
 */
export async function retryFailedBatches({
  result,
  signal,
  onProgress = () => {}
}) {
  if (!result?.chunks?.length || !result.failedChunks?.length) {
    throw new SmartImportError("Nothing to retry.");
  }

  const failedSet = new Set(result.failedChunks.map((f) => f.index));
  const subset = [];
  const labels = [];
  result.chunks.forEach((chunk, i) => {
    if (failedSet.has(i + 1)) {
      subset.push(chunk);
      labels.push(i + 1);
    }
  });

  const retried = await extractChunks({
    chunks: subset,
    images: result.images,
    signal,
    onProgress,
    startNumber: result.questions.length + 1,
    chunkLabels: labels,
    chunkCount: result.chunks.length
  });

  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const final = finalizeResult(
    [...result.questions, ...retried.questions],
    result.truncated
  );

  onProgress({ phase: "done" });

  return {
    ...result,
    questions: final.questions,
    failedChunks: retried.failedChunks,
    textOnlyChunks: result.textOnlyChunks + retried.textOnlyChunks,
    truncated: final.truncated,
    stats: computeStats(final.questions)
  };
}

// text-only fallback: hand out the chunk's images to its questions in order
function assignPositionalImages(questions, chunkImageIds) {
  if (!chunkImageIds.length) return;
  const withoutImages = questions.filter(
    (q) => !(q.image_ids || []).length
  );
  withoutImages.forEach((question, index) => {
    const imageId = chunkImageIds[index];
    if (imageId) question.image_ids = [imageId];
  });
}
